import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { Box } from '@chakra-ui/react';

import { Table, TableFilter } from '@app/components/common/table';
import { useAlert, useApi } from '@app/hooks';
import { DmpItem, DmpsReport, DmpsReportItem, DmpsTotal } from '@app/api/models';
import { API_FEAT_DATE_FORMAT } from '@app/utils';
import { DateRangeField, Paper, Txt } from '@app/components/common';
import { DateRange } from '@app/components/common/date-range-picker/types';
import Dialog from '@app/components/common/modals/dialog';
import { Level } from '@app/api/models/structure';
import { TableBase } from '@app/components/common/table/table-base';

import { useColumns } from './columns';
import { standsColumns } from './stands-columns';
import { DmpsNodeItem } from './types';
import { getFilterValue } from '../utils/filter';

export const DmpsReportTable = () => {
  const api = useApi();
  const { success } = useAlert();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<DateRange>({
    startDate: moment().subtract(1, 'day'),
    endDate: moment()
  });
  const [data, setData] = useState<DmpsReport>({
    total: {
      mount: 0,
    },
    report_data: {},
  });
  const [levels, setLevels] = useState<Level[]>([]);
  const [chain, setChain] = useState<DmpsNodeItem[]>([]);
  const [total, setTotal] = useState<DmpsTotal>({ mount: 0});
  const [filter, setFilter] = useState<TableFilter<DmpsNodeItem>[]>([
    { name: 'full_name', label: 'Пользователь', type: 'text', value: '', source: '', scope: 'include' },
    { name: 'director', label: 'Директор', type: 'text', value: '', source: '', scope: 'include' },
    { name: 'regional_manager', label: 'Региональный менеджер', type: 'text', value: '', source: '', scope: 'include' },
    { name: 'territorial_manager', label: 'Территориальный менеджер', type: 'text', value: '', source: '', scope: 'include' },
    { name: 'distributor', label: 'Дистрибьютор', type: 'autocomplete', value: '', source: 'distributor', scope: 'include' },
    {
      name: 'outlet', label: 'Торговая точка', type: 'outlet', value: '', scope: 'include',
      source: ''
    },
    {
      name: 'group_id', label: 'Группа', type: 'select', value: '', source: 'stand_dmp_group', scope: 'include',
    },
    {
      name: 'type_id', label: 'Тип', type: 'select', value: '', source: 'stand_dmp_type', scope: 'include',
    },
  ]);

  const [showDetail, setShowDetail] = useState<DmpsNodeItem>();

  const fetchData = useCallback(async ({ period, filter }: {
    period: DateRange,
    filter: TableFilter<DmpsNodeItem>[]
  }) => { 
    setLoading(true);
    const { data: levelsData } = await api.structure.getLevels({
      filter: {
        is_active: 'true',
      },
      sort: '-value',
      limit: 10,
      offset: 0,
    });

    setLevels([...levelsData.data, 
      {
        id: -1,
        name: 'Outlets',
        value: -1,
        is_active: true,
      }
    ]);
    setChain([]);

    const { data } = await api.reports.getDMPReport({ 
      filter: {
        date_b: period.startDate.format(API_FEAT_DATE_FORMAT),
        date_e: period.endDate.format(API_FEAT_DATE_FORMAT),
        full_name: getFilterValue(filter, 'full_name'),
        group_id: getFilterValue<DmpsNodeItem, number>(filter, 'group_id'),
        type_id: getFilterValue<DmpsNodeItem, number>(filter, 'type_id'),
        director: getFilterValue<DmpsNodeItem>(filter, 'director'),
        regional_manager: getFilterValue<DmpsNodeItem>(filter, 'regional_manager'),
        territorial_manager: getFilterValue<DmpsNodeItem>(filter, 'territorial_manager'),
        distributor_id: getFilterValue<DmpsNodeItem, number>(filter, 'distributor'),
        outlet_id: getFilterValue<DmpsNodeItem, number>(filter, 'outlet'),
      },
      download: false
    });
    setData(data);
    setTotal(data?.total || { mount: 0 });
    setLoading(false);
  }, []);

  const tableData = useMemo(() => {
    if (!data) {return [];}
    if (chain.length === 0) {
      return [...Object.keys(data.report_data).map<DmpsNodeItem>((key) => {
        return {
          id: key,
          title: data.report_data[key].employee?.name || 'Вакансия',
          subtitle: data.report_data[key].territory,
          level: data.report_data[key].level,
          total: data.report_data[key].total,
        };
      }),
        {
          id: '-1',
          title: 'Итого',
          subtitle: '',
          level: levels[0],
          total: data.total,
        }
      ];
    }

    let currentNode: DmpsReportItem = data.report_data[chain[0].id];

    for (const item of chain.slice(1)) {
      currentNode = currentNode.subordinates[item.id];
    }
    
    return [
      ...chain, 
      ...Object.keys(currentNode.subordinates ||  {}).map<DmpsNodeItem>((key) => ({
        id: key,
        title: key == currentNode.subordinates[key].level.name ? '' : currentNode.subordinates[key].employee?.name || 'Вакансия',
        subtitle: key == currentNode.subordinates[key].level.name ? '[пусто]' :  currentNode.subordinates[key].territory,
        level: currentNode.subordinates[key].level,
        total: currentNode.subordinates[key].total,
      })),
      ...Object.keys(currentNode.outlet || {}).map<DmpsNodeItem>((key) => {
        return {
          id: key,
          title: `[${key}] ` +  (currentNode.outlet[key]?.outlet?.name || 'ТТ неизвестно'),
          subtitle: currentNode.outlet[key]?.full_address || '-',
          level: levels[levels.length - 1],
          total: {
            mount: currentNode.outlet[key]?.total.mount || 0,
          },
        };
      }),
      {
        id: '-1',
        title: 'Итого',
        subtitle: '',
        level: levels[0],
        total: data.total,
      }
    ]; 
  }, [data, chain, levels]);

  const onFilterChange = useCallback((filter: TableFilter<DmpsNodeItem>[]) => {
    setFilter(filter);
    fetchData({ period, filter });
  }, [fetchData, period]);

  const onItemClick = useCallback((item: DmpsNodeItem) => {
    setChain([...chain, item]);
  }, [chain]);

  const onMoveUpClick = useCallback((item: DmpsNodeItem) => {
    const index = chain.findIndex(({ id }) => id === item.id);
    setChain(chain.slice(0, index));
  }, [chain]);

  const onShowDetail = useCallback((item: DmpsNodeItem) => {
   setShowDetail(item);
  }, []);

  const columns = useColumns({ chain, levels, onItemClick, onMoveUpClick, onShowDetail });


  const onChangePeriod = useCallback((period: DateRange) => {
    setPeriod(period);
    fetchData({ period, filter });
  }, [fetchData, filter]);

  const download = useCallback(async () => {
    setLoading(true);
    const { code, message } = await api.reports.getDMPReport({ 
      filter: {
        date_b: period.startDate.format(API_FEAT_DATE_FORMAT),
        date_e: period.endDate.format(API_FEAT_DATE_FORMAT),
        full_name: getFilterValue(filter, 'full_name'),
        group_id: getFilterValue<DmpsNodeItem, number>(filter, 'group_id'),
        type_id: getFilterValue<DmpsNodeItem, number>(filter, 'type_id'),
        director: getFilterValue<DmpsNodeItem>(filter, 'director'),
        regional_manager: getFilterValue<DmpsNodeItem>(filter, 'regional_manager'),
        territorial_manager: getFilterValue<DmpsNodeItem>(filter, 'territorial_manager'),
        distributor_id: getFilterValue<DmpsNodeItem, number>(filter, 'distributor'),
        outlet_id: getFilterValue<DmpsNodeItem, number>(filter, 'outlet'),
      },
      download: true
    });
    if (code === 200) {
      success(message);
    }
    setLoading(false);
  }, [period, filter]); 

  const scanningData = useMemo(() => {
    if (!showDetail) {return [];}

    const addOutlet = (outlet: EntityBase, scans: DmpItem[]) => {
      return scans.map((item) => ({ ...item, stands: { ...item, outlet } }));
    };

    const recur = (item?: DmpsReportItem) => {
      if (item?.subordinates) {return Object.values(item.subordinates).map(recur).flat();}
      if (item?.outlet) {
        return Object.values(item.outlet).map(({ dmps, outlet }) => addOutlet(outlet, dmps)).flat();
      }
      return [];
    };

    if (chain.length === 0 || showDetail.id === chain[0].id) { return recur(data.report_data[showDetail.id]); }

    let currentNode: DmpsReportItem = data.report_data[chain[0].id];

    for (const item of chain.slice(1)) {
      if (item.level.id === -1 || item.id === showDetail.id) { break; }
      currentNode = currentNode.subordinates[item.id];
    }

    if (showDetail.level.id === -1) {
      return addOutlet(currentNode.outlet[showDetail.id].outlet, currentNode.outlet[showDetail.id].dmps);
    }


    currentNode = currentNode.subordinates[showDetail.id];


    return recur(currentNode);
  }, [data, chain, showDetail]);

  return <>
    <Box mb={2}>
      <Paper>
        <Box px={6} py={4} display="flex">
          <Box mr={2} minWidth={300}>
            <DateRangeField
              id='period'
              label="Период"
              value={period}
              onChange={onChangePeriod}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
    <TableBase<DmpsNodeItem> 
      title="Отчет по ДМП"  
      columns={columns}
      data={tableData}
      loading={loading}
      onDownload={download}
      filter={filter}
      onFilterChange={onFilterChange}
    />
    <Dialog open={!!showDetail}
      onClose={() => setShowDetail(undefined)}
      title="Отчет по ДМП" 
      width={800}
    >
      <Box margin={-4}>
        <Table<DmpItem>
          title=""
          columns={standsColumns}
          data={scanningData}
          paper={false}
        />
      </Box>
    </Dialog>
  </>;
};